import React, { useRef, useState } from 'react'
import DepotoLogo from '../assets/img/group559.svg?react'
import BannerLogo from '../assets/img/dt-expe_login.png'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FormSelect, Toggle } from '../components'
import { Button } from '../components/shadcn/Button'
import { OAuthSession } from 'depoto-core'
import { useHistory, useLocation } from 'react-router-dom'
import { useCore, useLogin } from '../hooks'
import { DepotoCore } from 'depoto-core'
import { wait } from '../lib'
import { useForm } from 'react-hook-form'
import { ControlledInput } from '../components/Form/Controlled/ControlledInput'
import { Combox } from '../components/shadcn/combox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/shadcn/collapsible'

const serverTypes = [
  { label: 'Produkce', value: 'prod' },
  { label: 'Staging', value: 'stage' },
  { label: 'Development', value: 'dev' },
  { label: 'Custom', value: 'custom' },
]

type Props = {
  core: DepotoCore
}

type LoginFormValues = {
  username: string
  password: string
  customUrl: string
}

const isStaging = process.env.NODE_ENV === 'development' || window.location.hostname !== 'expedice.depoto.cz'

export const Login: React.FC<Props> = ({ core }) => {
  const [active, setActive] = useState(false)
  const [height, setHeight] = useState('0px')
  const [className, setclassName] = useState('overflow-auto')
  const [rotate, setRotate] = useState('transform duration-150 ease')
  const [server, setServer] = useState(isStaging ? 'prod' : 'prod')
  const [isRememberMe, setIsRememberMe] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [loginError, setLoginError] = useState<string>('')

  const history = useHistory()
  const location: any = useLocation()
  const { handleLogin, handleAuthInit, isLoggedIn } = useLogin()
  const { setSelectedChildCompany } = useCore()
  const { from } = location.state || { from: { pathname: '/' } }

  const contentSpace: any = useRef(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormValues>({
    defaultValues: {
      username: '',
      password: '',
      customUrl: 'https://',
    },
  })

  const customUrl = watch('customUrl')

  const toggleAccordion = () => {
    setActive(!active)
    setHeight(active ? '0px' : `${contentSpace?.current?.scrollHeight || 0}px`)
    setclassName(active ? 'overflow-auto' : 'overflow-visible mb-4')
    setRotate(active ? 'transform duration-150 ease' : 'transform duration-150 ease rotate-180')
  }

  const onSubmit = async (data: LoginFormValues) => {
    setIsFetching(true)
    setLoginError('')
    const isAuthenticated = await handleLogin(data.username, data.password, server, data.customUrl, core, isRememberMe)
    await wait(1000)
    setIsFetching(false)
    if (!isAuthenticated) {
      setLoginError('Chyba při přihlašování')
    } else {
      setLoginError('')
      setSelectedChildCompany(undefined)
      if (from?.pathname === '/login') {
        from.pathname = '/'
      }
      history.replace(from)
    }
  }

  return (
    <div className="grid grid-cols-3 h-screen">
      <div className="col-span-1 p-12 bg-sky-blue flex flex-col justify-between xs:hidden">
        <div className="grid grid-cols-1 gap-6">
          <div className="col-span-1 text-black font-black sm:text-5xl lg:text-6xl 2xl:text-8xl">Chytrá</div>
          <div className="col-span-1 text-white font-black sm:text-5xl lg:text-6xl 2xl:text-8xl">expediční</div>
          <div className="col-span-1 text-white font-black sm:text-5xl lg:text-6xl 2xl:text-8xl">aplikace</div>
        </div>
        <div className="flex justify-center items-center flex-1">
          <img src={BannerLogo} alt="Depoto Logo" className="sm:w-64 lg:w-auto object-contain" />
        </div>
      </div>

      <div className="col-span-2 flex justify-center items-center bg-base-blue xs:col-span-4">
        <div className="xs:p-0 mx-auto md:w-full md:max-w-md">
          <div className="w-[460px] p-8 bg-white shadow-md rounded-2xl opacity-100">
            <div className="flex flex-row justify-between mb-6">
              <div className="text-3xl text-black font-bold">Přihlášení</div>
              <DepotoLogo />
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col">
                {(loginError || errors.username || errors.password || errors.customUrl) && (
                  <div className="border border-input bg-background shadow-sm inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-md font-medium h-12 px-3 mb-4">
                    {loginError && <p className="text-red">{loginError}</p>}
                    {errors.username && <p className="text-red">{errors.username.message}</p>}
                    {errors.password && <p className="text-red">{errors.password.message}</p>}
                    {errors.customUrl && <p className="text-red">{errors.customUrl.message}</p>}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <ControlledInput
                    control={control}
                    name="username"
                    label="Uživatelské jméno"
                    type="text"
                    inputclassName="text-left"
                    rules={{ required: 'Uživatelské jméno je povinné' }}
                    onSubmit={() => handleSubmit(onSubmit)()}
                    debounceMs={0}
                  />
                  <ControlledInput
                    control={control}
                    name="password"
                    label="Heslo"
                    type="password"
                    inputclassName="text-left"
                    rules={{ required: 'Heslo je povinné' }}
                    onSubmit={() => handleSubmit(onSubmit)()}
                    debounceMs={0}
                  />
                </div>
                <Collapsible>
                  <div className={'flex justify-between items-center py-6'}>
                    <Toggle label={'Neodhlašovat'} value={isRememberMe} onChange={setIsRememberMe} />
                    <CollapsibleTrigger className="flex gap-2 items-center text-primary-blue font-normal cursor-pointer hover:text-blue-900">
                      Rozšířené nastavení
                      <FontAwesomeIcon icon={faChevronDown} size="lg" className={`${rotate} inline-block`} />
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="flex flex-col mb-6 gap-2">
                      <Combox value={server} items={serverTypes} onValueChange={setServer} disableSearch />
                      {server === 'custom' && (
                        <ControlledInput
                          control={control}
                          name="customUrl"
                          label={`Url (${customUrl}/oauth/v2/token)`}
                          type="text"
                          inputclassName="text-left"
                          rules={{
                            required: 'URL je povinné',
                            pattern: {
                              value: /^https?:\/\//,
                              message: 'URL musí začínat http:// nebo https://',
                            },
                          }}
                          onSubmit={() => handleSubmit(onSubmit)()}
                        />
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
              <Button
                variant="default"
                className={'w-full'}
                icon={'sign-in-alt'}
                title={'Přihlásit'}
                disabled={isFetching}
                type="submit">
                {'Přihlásit'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
